import { range, sort } from "@prismicio/editor-support/Array"
import { useRequest } from "@prismicio/editor-support/Suspense"
import {
  BlankSlate,
  BlankSlateDescription,
  BlankSlateIcon,
  BlankSlateTitle,
  Box,
  DefaultErrorMessage,
  Dialog,
  DialogContent,
  dialogErrorImage,
  ErrorBoundary,
  type FilterOption,
  FormInput,
  ScrollArea,
  Skeleton,
  Text,
  theme
} from "@prismicio/editor-ui"
import { Suspense } from "react"

import { documentStatus, getDocumentUrl } from "../../../core/document"
import { type DocumentMeta, getDocumentById } from "../../../core/service"
import { throwConfigError, useEditorConfig } from "../../../EditorConfig"
import { DocumentCard } from "./DocumentCard"
import { useDocuments } from "./documentsData"
import { DocumentsListFilters } from "./Filters"
import { useFilters } from "./useFilters"

interface DocumentsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelectDocument: (documentID: string) => void
  fixedCustomTypes?: Set<string>
  fixedTags?: Set<string>
  linkedDocumentId?: string
}

export function DocumentsModal(props: DocumentsModalProps) {
  const {
    onSelectDocument,
    fixedCustomTypes,
    fixedTags,
    linkedDocumentId,
    ...dialogProps
  } = props

  const config = useEditorConfig()

  const {
    searchTerm,
    filteredTags,
    setFilteredTags,
    filteredCustomTypes,
    setFilteredCustomTypes,
    filteredLocales,
    setFilteredLocales,
    typesFilter,
    tagsFilter
  } = useFilters(fixedCustomTypes, fixedTags)

  return (
    <Dialog
      size={{ height: 727, width: 656 }}
      hasClose={false}
      {...dialogProps}
    >
      <DialogContent>
        <Box flexDirection="column" height="100%">
          <Box
            padding={{ top: 16, inline: 16, bottom: 8 }}
            flexDirection="column"
            gap={12}
          >
            <FormInput
              value={searchTerm.value}
              onValueChange={searchTerm.set}
              placeholder="Search a page"
              icon="search"
            />
            {config.searchDocuments && (
              <DocumentsListFilters
                filteredTags={filteredTags}
                setFilteredTags={setFilteredTags}
                filteredCustomTypes={filteredCustomTypes}
                setFilteredCustomTypes={setFilteredCustomTypes}
                filteredLocales={filteredLocales}
                setFilteredLocales={setFilteredLocales}
                fixedTags={fixedTags}
                fixedCustomTypes={fixedCustomTypes}
              />
            )}
          </Box>
          {config.searchDocuments ? (
            <DocumentList
              searchTerm={searchTerm.debouncedValue}
              tagsFilter={tagsFilter}
              typesFilter={typesFilter}
              localesFilter={filteredLocales}
              onSelectDocument={onSelectDocument}
              linkedDocumentId={linkedDocumentId}
            />
          ) : (
            <EmptyList
              title="Oops!"
              message="Slice Machine does not yet support mock data for linked documents in the simulator."
            />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

interface DocumentListProps {
  searchTerm: string
  tagsFilter: Set<FilterOption>
  typesFilter: Set<FilterOption>
  localesFilter: Set<FilterOption>
  linkedDocumentId?: string
  onSelectDocument: (documentId: string) => void
}

function DocumentList(props: DocumentListProps) {
  const {
    searchTerm,
    tagsFilter,
    typesFilter,
    localesFilter,
    linkedDocumentId,
    onSelectDocument
  } = props

  return (
    <ScrollArea
      style={{
        flexGrow: 1,
        paddingBottom: theme.space[16],
        paddingInline: theme.space[12],
        display: "flex",
        flexDirection: "column"
      }}
    >
      {linkedDocumentId && (
        <>
          <StickyTitle>Linked page</StickyTitle>
          <ErrorBoundary
            renderError={() => (
              <FetchError description="An error occurred while fetching the linked page." />
            )}
          >
            <Suspense fallback={LinkedDocumentSkeleton}>
              <LinkedDocument
                linkedDocumentId={linkedDocumentId}
                tagsFilter={tagsFilter}
                onSelectDocument={onSelectDocument}
              />
            </Suspense>
          </ErrorBoundary>
        </>
      )}
      <StickyTitle>Link to a page</StickyTitle>
      <ErrorBoundary
        renderError={() => (
          <FetchError description="An error occurred while fetching pages." />
        )}
      >
        <Suspense fallback={LinkablePageListSkeleton}>
          <LinkableDocumentList
            searchTerm={searchTerm}
            tagsFilter={tagsFilter}
            localesFilter={localesFilter}
            typesFilter={typesFilter}
            linkedDocumentId={linkedDocumentId}
            onSelectDocument={onSelectDocument}
          />
        </Suspense>
      </ErrorBoundary>
    </ScrollArea>
  )
}

interface LinkableDocumentListProps {
  searchTerm: string
  tagsFilter: Set<FilterOption>
  localesFilter: Set<FilterOption>
  typesFilter: Set<FilterOption>
  linkedDocumentId?: string
  onSelectDocument: (documentId: string) => void
}

function LinkableDocumentList(props: LinkableDocumentListProps) {
  const {
    searchTerm,
    tagsFilter,
    localesFilter,
    typesFilter,
    linkedDocumentId,
    onSelectDocument
  } = props

  const { documents } = useDocuments({
    searchTerm,
    tagsFilter,
    localesFilter,
    typesFilter
  })

  const documentsWithoutLinked = documents.filter(
    (doc) => doc.id !== linkedDocumentId
  )

  if (documentsWithoutLinked.length === 0)
    return (
      <EmptyList
        title={documents.length === 1 ? "No other pages found!" : undefined}
      />
    )

  return (
    <Box flexDirection="column" gap={12}>
      {documentsWithoutLinked.map((document) => {
        const version = document?.versions[0]
        if (!version) return null
        return (
          <Document
            key={`${document.id}-${version.status}`}
            document={document}
            tagsFilter={tagsFilter}
            onSelectDocument={onSelectDocument}
          />
        )
      })}
    </Box>
  )
}

const LinkablePageListSkeleton = (
  <Box flexDirection="column" gap={12}>
    {range(1, 3).map((key) => (
      <Skeleton
        key={key}
        height={124}
        sx={{ marginInline: 4 }}
        // Negative z-index because StickyTitle creates a stacking context,
        // is before the Skeleton in the DOM, and we want to render the Skeleton below it.
        zIndex={-1}
      />
    ))}
  </Box>
)

interface FetchErrorDescription {
  description: string
}

function FetchError(props: FetchErrorDescription) {
  const { description } = props

  return (
    <Box flexDirection="column" alignItems="center" justifyContent="center">
      <DefaultErrorMessage
        title="Request failed"
        description={description}
        errorImageSrc={dialogErrorImage}
      />
    </Box>
  )
}

interface LinkedDocumentProps {
  linkedDocumentId: string
  tagsFilter: Set<FilterOption>
  onSelectDocument: (documentId: string) => void
}

function LinkedDocument(props: LinkedDocumentProps) {
  const { linkedDocumentId, tagsFilter, onSelectDocument } = props

  const selectedDocument = useDocumentData(linkedDocumentId)

  return (
    <Document
      document={selectedDocument}
      tagsFilter={tagsFilter}
      onSelectDocument={onSelectDocument}
      highlighted
    />
  )
}

const LinkedDocumentSkeleton = (
  <Skeleton
    height={124}
    sx={{ marginBlock: 4, marginInline: 4 }}
    // Negative z-index because StickyTitle creates a stacking context,
    // is before the Skeleton in the DOM, and we want to render the Skeleton below it.
    zIndex={-1}
  />
)

function useDocumentData(id: string) {
  const config = useEditorConfig()

  if (!config.searchDocuments) throwConfigError("searchDocuments")

  const { coreApiBaseUrl, authStrategy } = config

  return useRequest(getDocumentById, [coreApiBaseUrl, authStrategy, id])
}

interface DocumentProps {
  document?: DocumentMeta
  tagsFilter: Set<FilterOption>
  onSelectDocument: (documentId: string) => void
  highlighted?: boolean
}

function Document(props: DocumentProps) {
  const { document, tagsFilter, highlighted, onSelectDocument } = props
  const config = useEditorConfig()

  const version = document?.versions[0]
  if (!version) return null

  const documentUrl = getDocumentUrl({
    documentId: document.id,
    versionId: version.status === "archived" ? version.version_id : undefined,
    releaseId: version.status === "release" ? version.release_id : undefined,
    status: version.status,
    baseUrl: config.baseUrl
  })

  const sortedTags = sortTagsByFilteredTags({
    tags: version.tags,
    tagsFilter
  })

  return (
    <DocumentCard
      documentType={version.custom_type_label}
      documentUrl={documentUrl}
      description={version.preview_summary}
      tags={sortedTags}
      title={document.title}
      previewImageUrl={version.preview_image}
      onSelect={() => onSelectDocument(document.id)}
      statusColor={documentStatus[version.status].color}
      highlighted={highlighted}
    />
  )
}

interface StickyTitleProps {
  children: string
}

function StickyTitle(props: StickyTitleProps) {
  const { children } = props
  return (
    <Box
      position="sticky"
      top={0}
      backgroundColor="white"
      padding={{ block: 6, inline: 4 }}
    >
      <Text variant="smallBold" color="grey11">
        {children}
      </Text>
    </Box>
  )
}

interface EmptyListProps {
  title?: string
  message?: string
}

function EmptyList(props: EmptyListProps) {
  const {
    title = "No pages found!",
    message = "Consider using different keywords or tweaking your filters to find what you need."
  } = props

  return (
    <Box justifyContent="center" flexDirection="column" height="100%">
      <BlankSlate>
        <BlankSlateIcon
          name="close"
          lineColor="tomato11"
          backgroundColor="tomato4"
          size={48}
        />
        <BlankSlateTitle size="big">{title}</BlankSlateTitle>
        <BlankSlateDescription width={360}>{message}</BlankSlateDescription>
      </BlankSlate>
    </Box>
  )
}

interface SortTagsByFilteredTagsArgs {
  tagsFilter: Set<FilterOption>
  tags: string[]
}

export function sortTagsByFilteredTags(args: SortTagsByFilteredTagsArgs) {
  const { tags, tagsFilter } = args
  // Get filters by last added to map through
  const filterLabels = [...tagsFilter].map((tag) => tag.label).reverse()
  const sortedTags = sort(tags, (tag) => {
    // Get the tags in descending order
    return -filterLabels.indexOf(tag)
  })

  return sortedTags
}
